import { getHymnBySectionAndNumber } from "@/services/hymnsApi";
import { getHymnSlug } from "@/utils";
import {
  FavoriteHymn,
  getFavorites,
  MAX_FAVORITES,
  saveFavorites,
} from "./export-favorites";

export interface ParsedFavoriteHymn {
  section_code: string;
  section: string;
  number: number;
  title: string;
  url: string;
  rawLine: string;
  id?: string;
  category?: string | null;
  language?: string;
  media_url?: string | null;
  isAlreadyFavorited?: boolean;
}

export interface ParseFavoritesResult {
  validItems: ParsedFavoriteHymn[];
  invalidLines: string[];
  totalParsed: number;
  duplicateCount: number;
}

export const KNOWN_SECTION_NAMES: Record<string, string> = {
  "CE-CR": "Chants d'Espérance (Kreyòl)",
  "CE-FR": "Chants d'Espérance (Français)",
  "MJ-CR": "Mélodies Joyeuses (Kreyòl)",
  "MJ-FR": "Mélodies Joyeuses (Français)",
  "RN-CR": "Réveillons-Nous (Kreyòl)",
  "RN-FR": "Réveillons-Nous (Français)",
  "RNC-CR": "Réveillons-Nous Chrétiens (Kreyòl)",
  "VR-CR": "Voix du Réveil (Kreyòl)",
  "VR-FR": "Voix du Réveil (Français)",
  "HC-CR": "Haïti Chante avec Radio Lumière (Kreyòl)",
  EE: "Écho des Élus",
  GA: "Gloire à l'Agneau",
  OR: "Ombre du Refuge",
  RD: "Réveil des Derniers Jours",
  TC: "Triomphe du Calvaire",
  CRX: "Chants du Retour du Christ",
  "CB-FR": "Cantiques de la Bénédiction",
  "CJ-FR": "Chants Joyeux (Français)",
  "CJ-CR": "Chants Joyeux (Kreyòl)",
};

/**
 * Format a slug into a readable title string
 * e.g. "54-leve-nanm-mwen-ak-soley-la" -> "Leve nanm mwen ak solèy la"
 */
function cleanSlugToTitle(slug: string): string {
  if (!slug) return "";
  // Strip leading number and dash if present
  const withoutNumber = slug.replace(/^\d+-/, "");
  const words = withoutNumber
    .split("-")
    .map((w) => w.trim())
    .filter(Boolean);
  if (words.length === 0) return "";
  const sentence = words.join(" ");
  return sentence.charAt(0).toUpperCase() + sentence.slice(1);
}

/**
 * Parse a single line from an exported favorites text or list of links.
 */
export function parseFavoriteLine(rawLine: string): ParsedFavoriteHymn | null {
  const line = rawLine.trim();
  if (!line) return null;

  // 1. Look for URL pattern:
  // e.g. https://egarizim.com/chants-desperance/CE-CR/54-leve-nanm-mwen-ak-soley-la
  // or /chants-desperance/CE-CR/54
  const urlRegex =
    /(?:https?:\/\/[^\s/]+)?\/chants-desperance\/([^/\s#?]+)\/(\d+)(?:-([^\s#?)]+))?/i;
  const urlMatch = line.match(urlRegex);

  let sectionCode = "";
  let number = 0;
  let title = "";
  let section = "";
  let url = "";

  if (urlMatch) {
    sectionCode = decodeURIComponent(urlMatch[1]).trim().toUpperCase();
    number = parseInt(urlMatch[2], 10);
    const slug = urlMatch[3] ? decodeURIComponent(urlMatch[3]) : "";
    url = urlMatch[0].startsWith("http")
      ? urlMatch[0]
      : `https://egarizim.com${urlMatch[0]}`;

    if (slug) {
      title = cleanSlugToTitle(slug);
    }
  }

  // 2. Check for badge [SECTION_CODE - Section Name] or [SECTION_CODE]
  const badgeMatch = line.match(/\[([^\]]+)\]/);
  if (badgeMatch) {
    const badgeContent = badgeMatch[1].trim();
    if (badgeContent.includes(" - ")) {
      const parts = badgeContent.split(" - ");
      const codeFromBadge = parts[0].trim().toUpperCase();
      if (!sectionCode) sectionCode = codeFromBadge;
      section = parts.slice(1).join(" - ").trim();
    } else {
      const codeFromBadge = badgeContent.trim().toUpperCase();
      if (!sectionCode) sectionCode = codeFromBadge;
    }
  }

  // 3. Check for number in text e.g. "N° 54" or "No 54" or "#54"
  const numberTextMatch = line.match(/(?:N°|No|#)\s*(\d+)/i);
  if (numberTextMatch) {
    number = parseInt(numberTextMatch[1], 10);
  }

  // 4. Check for title in line text:
  // Usually: "N° 54 - Leve, nanm mwen, ak solèy la: http..."
  // Or: "[Title](http...)"
  const markdownTitleMatch = line.match(/\[([^\]]+)\]\s*\(/);
  if (markdownTitleMatch && !markdownTitleMatch[1].includes(" - ")) {
    title = markdownTitleMatch[1].trim();
  }

  const titleTextMatch = line.match(
    /(?:N°|No|#)\s*\d+\s*-\s*([^:]+?)(?:\s*:\s*https?|\s*:\s*\/|\s*$)/i
  );
  if (titleTextMatch) {
    const candidate = titleTextMatch[1].trim();
    if (candidate) {
      title = candidate;
    }
  }

  // Fallback: If sectionCode was not found, or number is invalid, cannot parse
  if (!sectionCode || !number || isNaN(number) || number <= 0) {
    return null;
  }

  // Provide readable fallback title
  if (!title) {
    title = `Cantique N° ${number}`;
  }

  // Provide fallback section name from catalog
  if (!section) {
    section = KNOWN_SECTION_NAMES[sectionCode] || sectionCode;
  }

  // Fallback URL if missing
  if (!url) {
    const slug = getHymnSlug(number, title);
    url = `https://egarizim.com/chants-desperance/${encodeURIComponent(
      sectionCode
    )}/${slug}`;
  }

  return {
    section_code: sectionCode,
    section,
    number,
    title,
    url,
    rawLine: line,
  };
}

/**
 * Parse an entire pasted text into valid favorite hymns and invalid lines.
 * Handles deduplication within the pasted content and marks items already in favorites.
 */
export function parseFavoritesText(rawText: string): ParseFavoritesResult {
  if (!rawText || !rawText.trim()) {
    return {
      validItems: [],
      invalidLines: [],
      totalParsed: 0,
      duplicateCount: 0,
    };
  }

  const lines = rawText
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);

  const currentFavorites = getFavorites();
  const validMap = new Map<string, ParsedFavoriteHymn>();
  const invalidLines: string[] = [];
  let duplicateCount = 0;

  for (const line of lines) {
    // Skip empty lines or pure separators/headers
    if (line.startsWith("---") || line.startsWith("===")) {
      continue;
    }

    const parsed = parseFavoriteLine(line);
    if (!parsed) {
      invalidLines.push(line);
      continue;
    }

    const key = `${parsed.section_code.toUpperCase()}-${parsed.number}`;
    if (validMap.has(key)) {
      duplicateCount++;
      continue;
    }

    // Check if already present in stored favorites
    const isAlreadyFavorited = currentFavorites.some(
      (f) =>
        f.section_code.toUpperCase() === parsed.section_code.toUpperCase() &&
        f.number === parsed.number
    );

    parsed.isAlreadyFavorited = isAlreadyFavorited;
    validMap.set(key, parsed);
  }

  const validItems = Array.from(validMap.values());

  return {
    validItems,
    invalidLines,
    totalParsed: validItems.length,
    duplicateCount,
  };
}

/**
 * Enriches parsed hymns by querying the Supabase REST API in parallel.
 * Retrieves verified titles, audio media URLs, language, and IDs.
 */
export async function enrichFavoritesWithApi(
  items: ParsedFavoriteHymn[]
): Promise<ParsedFavoriteHymn[]> {
  if (items.length === 0) return items;

  try {
    const promises = items.map(async (item) => {
      try {
        const hymn = await getHymnBySectionAndNumber(
          item.section_code,
          item.number
        );
        if (hymn) {
          return {
            ...item,
            id: hymn.id || item.id,
            title: hymn.title || item.title,
            section: hymn.section || item.section,
            category: hymn.category || item.category,
            language: hymn.language || item.language,
            media_url: hymn.media_url ?? null,
          };
        }
      } catch (err) {
        // Silently keep parsed details on network/API failure
        console.warn(
          `Could not enrich hymn ${item.section_code} N° ${item.number}`,
          err
        );
      }
      return item;
    });

    return await Promise.all(promises);
  } catch {
    return items;
  }
}

/**
 * Apply the import of parsed hymns to localStorage favorites.
 * Supports:
 * - 'merge': Adds imported hymns at top, keeps existing, avoids duplicates.
 * - 'replace': Overwrites favorites with imported hymns.
 */
export function applyImportFavorites(
  itemsToImport: ParsedFavoriteHymn[],
  mode: "merge" | "replace" = "merge"
): {
  success: boolean;
  count: number;
  favorites: FavoriteHymn[];
} {
  if (!itemsToImport || itemsToImport.length === 0) {
    return {
      success: false,
      count: 0,
      favorites: getFavorites(),
    };
  }

  const now = Date.now();
  const newEntries: FavoriteHymn[] = itemsToImport.map((item, idx) => ({
    id: item.id,
    section_code: item.section_code,
    section: item.section,
    number: item.number,
    title: item.title,
    category: item.category,
    language: item.language,
    media_url: item.media_url ?? null,
    // Stagger addedAt so order is preserved
    addedAt: now - idx * 100,
  }));

  let updatedList: FavoriteHymn[] = [];

  if (mode === "replace") {
    updatedList = newEntries;
  } else {
    // Mode 'merge':
    const current = getFavorites();
    // Exclude existing favorites that match newly imported items
    const filteredCurrent = current.filter(
      (c) =>
        !newEntries.some(
          (n) =>
            n.section_code.toUpperCase() === c.section_code.toUpperCase() &&
            n.number === c.number
        )
    );
    // Put new items at the top
    updatedList = [...newEntries, ...filteredCurrent];
  }

  saveFavorites(updatedList);

  return {
    success: true,
    count: newEntries.length,
    favorites: updatedList,
  };
}
